import React, { useEffect } from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FaMotorcycle, FaStore, FaToggleOn, FaToggleOff, FaGlobe } from 'react-icons/fa';
import './SalesChannelsConfig.scss';

interface SalesChannelsConfigProps {
  isDelivery: boolean;
  setIsDelivery: (value: boolean) => void;
  isDesk: boolean;
  setIsDesk: (value: boolean) => void;
  isOnline?: boolean;
  setIsOnline?: (value: boolean) => void;
  price_delivery: string;
  setPriceDelivery: (value: string) => void;
  price_online?: string;
  setPriceOnline?: (value: string) => void;
  priceDesk: string;
  setPriceDesk: (value: string) => void;
  basePrice?: string; // Add base price prop for inheritance
  currentItemId?: number | string; // Add to detect update mode
  // Third-party provider specific pricing
  thirdPartyProviders?: { third_party_provider_id: number; name: string }[];
  thirdPartyPrices?: { [providerId: number]: string };
  onThirdPartyPriceChange?: (providerId: number, price: string) => void;
  onThirdPartyPriceBlur?: (providerId: number) => void;
}

const SalesChannelsConfig: React.FC<SalesChannelsConfigProps> = ({
  isDelivery,
  setIsDelivery,
  isDesk,
  setIsDesk,
  isOnline,
  setIsOnline,
  price_delivery,
  setPriceDelivery,
  price_online,
  setPriceOnline,
  priceDesk,
  setPriceDesk,
  basePrice,
  currentItemId,
  thirdPartyProviders = [],
  thirdPartyPrices = {},
  onThirdPartyPriceChange,
  onThirdPartyPriceBlur,
}) => {
  const [showProviderPrices, setShowProviderPrices] = React.useState(false);

  // Ensure online channel is active by default when parent did not provide a value
  useEffect(() => {
    if (typeof isOnline === 'undefined' && setIsOnline) {
      setIsOnline(true);
    }
  }, [isOnline, setIsOnline]);

  // Initialize price_online if it's undefined
  useEffect(() => {
    if (typeof price_online === 'undefined' && setPriceOnline) {
      setPriceOnline('');
    }
  }, [price_online, setPriceOnline]);
  
  // Inherit base price when it changes - ONLY in create mode (not update mode)
  useEffect(() => {
    // Skip auto-inheritance if we're in update mode (currentItemId exists)
    if (currentItemId) return;
    
    if (basePrice) {
      setPriceDelivery(basePrice);
      setPriceDesk(basePrice);
      if (setPriceOnline) setPriceOnline(basePrice);
    }
  }, [basePrice, setPriceDelivery, setPriceDesk, setPriceOnline, currentItemId]);

  const handleDeliveryPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPriceDelivery(e.target.value);
  };

  const handleOnlinePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setPriceOnline) {
      setPriceOnline(e.target.value);
    }
  };

  const handleDeskPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPriceDesk(e.target.value);
  };

  return (
    <div className="sales-channels-config">
      <div className="availability-section">
        <h6 className="availability-title">Canales de Venta</h6>
        <Row className="g-3">
          <Col lg={4} xs={12}>
            <div className="config-card availability-card">
              <div className="config-item">
                <div className="config-label">
                  <FaMotorcycle className="config-icon delivery" />
                  <div className="label-content">
                    <span className="label-title">Disponible para delivery</span>
                    <span className="label-subtitle">Venta a domicilio</span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => setIsDelivery(!isDelivery)}>
                  {isDelivery ? (
                    <FaToggleOn className="toggle-icon active" />
                  ) : (
                    <FaToggleOff className="toggle-icon inactive" />
                  )}
                </div>
              </div>
              
              {isDelivery && (
                <div className="price-input-section">
                  <Form.Label>Precio delivery</Form.Label>
                  <InputGroup className="modern-price-input">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={price_delivery}
                      placeholder="0.00"
                      onChange={handleDeliveryPriceChange}
                      min="0"
                      step="0.01"
                    />
                  </InputGroup>
                </div>
              )}

              {isDelivery && thirdPartyProviders.length > 0 && onThirdPartyPriceChange && (
                <div className="provider-prices-section">
                  <div 
                    className="provider-prices-toggle"
                    onClick={() => setShowProviderPrices(!showProviderPrices)}
                  >
                    <span className="toggle-label">
                      {showProviderPrices ? '▼' : '▶'} Precios por Aplicación
                    </span>
                    <span className="toggle-hint">(Opcional)</span>
                  </div>
                  {showProviderPrices && (
                    <div className="provider-prices-list">
                      <p className="provider-prices-help">
                        Deja vacío para usar el precio de delivery
                      </p>
                      {thirdPartyProviders.map((provider) => (
                        <div key={provider.third_party_provider_id} className="provider-price-row">
                          <span className="provider-name">{provider.name}</span>
                          <InputGroup className="provider-price-input">
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                              type="number"
                              value={thirdPartyPrices[provider.third_party_provider_id] || ''}
                              placeholder={price_delivery || '0.00'}
                              onChange={(e) => onThirdPartyPriceChange(provider.third_party_provider_id, e.target.value)}
                              onBlur={() => onThirdPartyPriceBlur && onThirdPartyPriceBlur(provider.third_party_provider_id)}
                              min="0"
                              step="0.01"
                            />
                          </InputGroup>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Col>

          <Col lg={4} xs={12}>
            <div className="config-card availability-card">
              <div className="config-item">
                <div className="config-label">
                  <FaStore className="config-icon desk" />
                  <div className="label-content">
                    <span className="label-title">Disponible en caja</span>
                    <span className="label-subtitle">Venta en local</span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => setIsDesk(!isDesk)}>
                  {isDesk ? (
                    <FaToggleOn className="toggle-icon active" />
                  ) : (
                    <FaToggleOff className="toggle-icon inactive" />
                  )}
                </div>
              </div>
              
              {isDesk && (
                <div className="price-input-section">
                  <Form.Label>Precio en caja</Form.Label>
                  <InputGroup className="modern-price-input">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={priceDesk}
                      placeholder="0.00"
                      onChange={handleDeskPriceChange}
                      min="0"
                      step="0.01"
                    />
                  </InputGroup>
                </div>
              )}
            </div>
          </Col>

          <Col lg={4} xs={12}>
            <div className="config-card availability-card">
              <div className="config-item">
                <div className="config-label">
                  <FaGlobe className="config-icon online" />
                  <div className="label-content">
                    <span className="label-title">Disponible online</span>
                    <span className="label-subtitle">Venta en plataforma online</span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => setIsOnline && setIsOnline(!isOnline)}>
                  {isOnline ? (
                    <FaToggleOn className="toggle-icon active" />
                  ) : (
                    <FaToggleOff className="toggle-icon inactive" />
                  )}
                </div>
              </div>
              
              {isOnline && (
                <div className="price-input-section">
                  <Form.Label>Precio online</Form.Label>
                  <InputGroup className="modern-price-input">
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={price_online || ''}
                      placeholder="0.00"
                      onChange={handleOnlinePriceChange}
                      min="0"
                      step="0.01"
                      disabled={!setPriceOnline}
                    />
                  </InputGroup>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SalesChannelsConfig;
